// src/components/catalog/ToolCard.jsx
import React from 'react';
import { ArrowRight, MonitorDown, Star } from 'lucide-react';
import ToolIcon from '../common/ToolIcon';
import { cx } from '../common/ui';
import { hasDesktopBuild } from '../../services/desktop';
import { tileColor } from '../../services/toolIcons';

const FavButton = ({ isFavorite, onToggle, toolName, className }) => (
  <button
    type="button"
    aria-pressed={isFavorite}
    aria-label={isFavorite ? `Unstar ${toolName}` : `Star ${toolName}`}
    title={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    className={cx(
      'grid h-7 w-7 flex-none place-items-center rounded-md transition-colors',
      isFavorite ? 'text-gold' : 'text-ink-3 hover:bg-surface-2 hover:text-gold',
      className
    )}
  >
    <Star className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} />
  </button>
);

const ToolCard = ({ tool, category, isFavorite, onToggleFavorite, onOpen, layout = 'grid' }) => {
  // Worth flagging on the card: it changes what clicking it does.
  const desktop = hasDesktopBuild(tool);
  const open = () => onOpen(tool);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  if (layout === 'list') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={onKeyDown}
        className="flex cursor-pointer items-center gap-3.5 border-b border-line px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-2"
      >
        <span className="cat-tile h-8 w-8" data-color={tileColor(tool, category)}>
          <ToolIcon tool={tool} fill className="h-[17px] w-[17px]" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{tool.name}</p>
          <p className="truncate text-[13px] text-ink-2">{tool.description}</p>
        </div>

        {desktop && (
          <MonitorDown
            className="hidden h-4 w-4 flex-none text-ink-3 sm:block"
            aria-label="Also available as a desktop app"
          />
        )}

        <span className="hidden items-center gap-2 text-xs text-ink-3 sm:flex">
          <span className="cat-dot" data-color={category.color} />
          {category.label}
        </span>

        <FavButton
          isFavorite={isFavorite}
          onToggle={onToggleFavorite}
          toolName={tool.name}
        />

        <ArrowRight className="h-4 w-4 flex-none text-ink-3" />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeyDown}
      className="group relative flex cursor-pointer flex-col gap-2 rounded-card border border-line bg-surface p-4 transition-all hover:-translate-y-px hover:border-line-strong hover:shadow-lift"
    >
      <FavButton
        isFavorite={isFavorite}
        onToggle={onToggleFavorite}
        toolName={tool.name}
        className="absolute right-3 top-3"
      />

      <span className="cat-tile h-9 w-9" data-color={tileColor(tool, category)}>
        <ToolIcon tool={tool} fill className="h-[19px] w-[19px]" />
      </span>

      <h3 className="mt-1 pr-8 text-[15.5px] font-semibold leading-snug">{tool.name}</h3>
      <p className="flex-1 text-[13.5px] leading-relaxed text-ink-2">{tool.description}</p>

      <div className="mt-1 flex items-center justify-between gap-3 border-t border-line pt-2.5">
        <span className="flex min-w-0 items-center gap-2 text-xs text-ink-3">
          <span className="cat-dot" data-color={category.color} />
          <span className="truncate">{category.label}</span>
          {desktop && (
            <MonitorDown
              className="h-3.5 w-3.5 flex-none"
              aria-label="Also available as a desktop app"
            />
          )}
        </span>
        <span className="flex flex-none items-center gap-1.5 text-[13px] font-medium text-accent">
          Open
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
};

export default ToolCard;
